<template>
  <div>
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item :to="{ name: 'Home' }">
        Mon compte
      </el-breadcrumb-item>

      <el-breadcrumb-item :to="{ name: 'Listes' }">
        Mes listes cadeau
      </el-breadcrumb-item>

      <el-breadcrumb-item v-if="!!liste">
        {{ liste.nameCagnotte }}
      </el-breadcrumb-item>
    </el-breadcrumb>

    <el-row
      v-if="underMaintenance"
      type="flex"
    >
      <el-alert
        :closable="false"
        effect="dark"
        show-icon
        title="Maintenance"
        type="info"
      >
        Nos services sont momentanément indisponibles en raison d'activité de maintenance.<br>
        Le site sera à nouveau accessible dans quelques heures.
      </el-alert>
    </el-row>

    <el-divider />

    <template v-if="!!liste">
      <el-dialog
        :title="`Message déposé par ${modals.message.value.name}`"
        :visible.sync="modals.message.show"
      >
        <p class="vv-liste__message-text">
          {{ modals.message.value.texte }}
        </p>
      </el-dialog>

      <el-dialog
        title="Votre liste cadeau est créée !"
        :visible.sync="modals.created.show"
      >
        <p class="vv-liste__modals-created-link-info">
          Voici la page web de la liste cadeau à partager avec les participants :
        </p>
        <participation-url
          :id="liste.id"
          button-is-primary
          @copy="displayCopiedToClipboardMessage"
        />
        <p class="vv-liste__modals-created-eu-info">
          Vous pourrez suivre les participations à partir de votre espace personnel en ligne dans la présente rubrique "Mes listes cadeau".
        </p>
      </el-dialog>

      <el-row
        v-if="liste.userIsCreateur()"
        type="flex"
      >
        <el-col :span="24">
          <el-card
            shadow="never"
            :class="`vv-liste__card-nom ${liste.etatCagnotte ? 'vv-liste__card-nom-has-no-body' : ''}`"
          >
            <template #header>
              <span>{{ liste.dynamicNameBasedOnUserStatus() }}</span>
            </template>

            <template v-if="!liste.etatCagnotte">
              <el-alert
                :closable="false"
                effect="dark"
                show-icon
                title="Cette liste cadeau est clôturée"
                type="warning"
              />
            </template>

            <div />
          </el-card>
        </el-col>
      </el-row>

      <el-row type="flex">
        <el-col :span="24">
          <el-card shadow="never">
            <template #header>
              <div class="vv-liste__participants-wrapper">
                <span>{{ liste.userIsBeneficiaireButNotCreateur() ? liste.nameForBeneficiaireButNotCreateur() : 'Participations' }}</span>

                <el-tooltip
                  v-if="liste.userIsCreateur()"
                  content="Actualiser les participations"
                  placement="top"
                >
                  <i
                    class="el-icon-refresh-right"
                    @click="refreshListe"
                  />
                </el-tooltip>

                <el-button
                  v-if="!liste.userIsCreateur() && !!liste.bonCadeau"
                  size="small"
                  type="primary"
                  :loading="forms.beneficiaireCoupon.loading"
                  :disabled="underMaintenance"
                  @click="downloadCouponAsBeneficiaire"
                >
                  Télécharger le bon cadeau
                  <i class="el-icon-download el-icon-right" />
                </el-button>
              </div>
            </template>

            <el-empty
              v-if="!liste.donnations || liste.donnations.length <= 0"
              description="Aucune participation"
            />

            <template v-else>
              <el-row
                class="vv-liste__participants-mobile"
                :gutter="20"
              >
                <el-col
                  :span="24"
                >
                  <el-collapse accordion>
                    <el-collapse-item
                      v-for="participation in liste.donnations"
                      :key="participation.id"
                      :title="participation.name"
                    >
                      <el-descriptions
                        :column="1"
                        border
                      >
                        <el-descriptions-item label="Date">
                          {{ participation.date | date }}
                        </el-descriptions-item>
                        <el-descriptions-item label="Montant">
                          {{ participation.montant }} €
                        </el-descriptions-item>
                        <el-descriptions-item
                          v-if="!!participation.textParticipation && participation.textParticipation.length"
                          label="Message"
                        >
                          <el-button
                            @click="() => {
                              modals.message.value.name = participation.name
                              modals.message.value.texte = participation.textParticipation
                              modals.message.show = true
                            }"
                          >
                            Lire le message
                          </el-button>
                        </el-descriptions-item>
                      </el-descriptions>
                    </el-collapse-item>
                  </el-collapse>
                </el-col>
              </el-row>
              <el-table
                ref="donationTable"
                stripe
                class="vv-liste__participants-desktop"
                :max-height="500"
                empty-text="Aucune donation"
                :data="liste.donnations"
              >
                <el-table-column
                  prop="date"
                  label="Date"
                  sortable
                  width="240"
                >
                  <template slot-scope="scope">
                    {{ scope.row.date | date }}
                  </template>
                </el-table-column>
                <el-table-column
                  prop="name"
                  label="Participant"
                  sortable
                  width="420"
                />
                <el-table-column
                  v-if="liste.boolShowMontantGift || liste.userIsCreateur()"
                  prop="montant"
                  label="Montant"
                  sortable
                  width="180"
                >
                  <template slot-scope="scope">
                    {{ scope.row.montant }} €
                  </template>
                </el-table-column>
                <el-table-column
                  prop="textParticipation"
                  label="Message"
                  align="left"
                >
                  <template slot-scope="scope">
                    <span class="vv-liste__participants-textParticipation">
                      {{ scope.row.textParticipation }}
                    </span>
                  </template>
                </el-table-column>
              </el-table>
              <el-row
                :gutter="20"
                class="vv-liste__participants-stats"
              >
                <el-col
                  :md="liste.userIsBeneficiaire() && !liste.etatCagnotte ? 8 : 12"
                  :sm="24"
                >
                  <div class="vv-liste__participants-stats-stat">
                    <span class="vv-liste__participants-stats-stat-title">Nombre de participations</span>
                    <span class="vv-liste__participants-stats-stat-value">{{ liste.donnations.length }}</span>
                  </div>
                </el-col>
                <el-col
                  :md="liste.userIsBeneficiaire() && !liste.etatCagnotte ? 8 : 12"
                  :sm="24"
                >
                  <div class="vv-liste__participants-stats-stat">
                    <span class="vv-liste__participants-stats-stat-title">Montant total</span>
                    <span class="vv-liste__participants-stats-stat-value">{{ liste.donnations.reduce((s, d) => s + d.montant, 0) | currency(true) }}</span>
                  </div>
                </el-col>
                <el-col
                  v-if="liste.userIsBeneficiaire() && !liste.etatCagnotte"
                  :md="8"
                  :sm="24"
                >
                  <div class="vv-liste__participants-stats-stat">
                    <span class="vv-liste__participants-stats-stat-title">Solde</span>
                    <span class="vv-liste__participants-stats-stat-value">{{ liste.totalSolde | currency(true) }}</span>
                  </div>
                </el-col>
              </el-row>
            </template>
          </el-card>
        </el-col>
      </el-row>

      <el-row type="flex">
        <el-col :span="24">
          <el-row
            :gutter="20"
            type="flex"
          >
            <el-col
              :xs="24"
              :md="liste.etatCagnotte && liste.userIsCreateur() ? 12 : 24"
            >
              <el-row v-if="liste.etatCagnotte && liste.userIsCreateur()">
                <el-card shadow="never">
                  <template #header>
                    <span>Lien vers la liste cadeau</span>
                  </template>

                  <participation-url
                    :id="liste.id"
                    @copy="displayCopiedToClipboardMessage"
                  />
                </el-card>
              </el-row>
              <el-row v-if="liste.userIsCreateur()">
                <el-card shadow="never">
                  <template #header>
                    <span>Bon cadeau à offrir</span>
                  </template>

                  <el-alert
                    v-if="liste.userIsCreateurButNotBeneficiaire()"
                    type="info"
                    :closable="false"
                  >
                    {{
                      !liste.etatCagnotte && liste.userIsCreateurButNotBeneficiaire()
                        ? 'Ces informations sont visibles par le bénéficiaire depuis son espace personnel en ligne.'
                        : 'Ces informations seront visibles par le bénéficiaire depuis son espace personnel en ligne après la clôture de la liste cadeau.'
                    }}
                  </el-alert>

                  <ValidationObserver v-slot="{ passes }">
                    <el-form>
                      <el-row
                        :gutter="20"
                        type="flex"
                      >
                        <el-col :span="24">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="coupon.titre"
                            rules="max:500"
                          >
                            <el-form-item
                              :error="errors[0]"
                              label="Titre du bon cadeau"
                            >
                              <el-input
                                v-model="forms.coupon.input.titre"
                                placeholder="Titre"
                                :disabled="underMaintenance || forms.coupon.saving"
                              />
                            </el-form-item>
                          </ValidationProvider>
                        </el-col>
                      </el-row>

                      <el-row
                        :gutter="20"
                        type="flex"
                      >
                        <el-col :span="24">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="texte"
                            rules="max:1500"
                          >
                            <el-form-item
                              :error="errors[0]"
                              label="Texte de personnalisation du bon cadeau"
                            >
                              <el-input
                                v-model="forms.coupon.input.texte"
                                rows="5"
                                placeholder="Mon texte"
                                type="textarea"
                                resize="none"
                                :disabled="underMaintenance || forms.coupon.saving"
                              />
                            </el-form-item>
                          </ValidationProvider>
                        </el-col>
                      </el-row>

                      <el-row
                        :gutter="20"
                        type="flex"
                      >
                        <el-col
                          v-if="liste.userIsCreateur()"
                          :span="24"
                        >
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="showDons"
                            rules="required"
                          >
                            <el-form-item :error="errors[0]">
                              <el-checkbox
                                v-model="forms.coupon.input.showDons"
                                label="Afficher le détail des montants des participations dans le bon cadeau"
                                :disabled="forms.coupon.saving || underMaintenance"
                              />
                            </el-form-item>
                          </ValidationProvider>
                        </el-col>
                        <el-col :span="24">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="showTotal"
                            rules="required"
                          >
                            <el-form-item :error="errors[0]">
                              <el-checkbox
                                v-model="forms.coupon.input.showTotal"
                                label="Afficher le montant total de la liste dans le bon cadeau"
                                :disabled="underMaintenance || forms.coupon.saving"
                              />
                            </el-form-item>
                          </ValidationProvider>
                        </el-col>
                      </el-row>

                      <el-row
                        type="flex"
                      >
                        <el-col
                          align="center"
                          :span="24"
                        >
                          <el-button
                            v-loading="forms.coupon.saving"
                            size="small"
                            type="primary"
                            :disabled="underMaintenance"
                            @click="passes(saveAndDownloadCoupon)"
                          >
                            Enregistrer et télécharger
                            <i class="el-icon-download el-icon-right" />
                          </el-button>
                        </el-col>
                      </el-row>
                    </el-form>
                  </ValidationObserver>
                </el-card>
              </el-row>
            </el-col>

            <el-col
              :class="{ 'vv-liste__columns-second': true, 'vv-liste__columns-second-multi-row': !liste.etatCagnotte || !liste.userIsCreateur() }"
              :xs="24"
              :md="liste.etatCagnotte && liste.userIsCreateur() ? 12 : 24"
            >
              <el-row v-if="liste.userIsCreateur()">
                <!-- Modifier les paramètres -->
                <el-card shadow="never">
                  <template #header>
                    <span>Paramètres</span>
                  </template>

                  <el-row>
                    <el-col :span="24">
                      <ValidationObserver v-slot="{ passes }">
                        <el-form>
                          <template v-if="liste.userIsCreateurButNotBeneficiaire()">
                            <el-row
                              :gutter="20"
                              type="flex"
                            >
                              <el-col :span="24">
                                <el-form-item
                                  label="Nom du bénéficiaire"
                                >
                                  <el-input
                                    v-model="liste.nameBeneficiaire"
                                    placeholder="Nom du bénéficiaire"
                                    disabled
                                  />
                                </el-form-item>
                              </el-col>
                            </el-row>
                            <el-row
                              :gutter="20"
                              type="flex"
                            >
                              <el-col :span="24">
                                <el-form-item
                                  label="Adresse email du bénéficiaire"
                                >
                                  <el-input
                                    v-model="liste.emailBeneficiaire"
                                    placeholder="Adresse email du bénéficiaire"
                                    disabled
                                  />
                                </el-form-item>
                              </el-col>
                            </el-row>
                          </template>

                          <el-row
                            :gutter="20"
                            type="flex"
                          >
                            <el-col :span="24">
                              <ValidationProvider
                                v-slot="{ errors }"
                                name="cagnotte.nom"
                                rules="required"
                              >
                                <el-form-item
                                  :error="errors[0]"
                                  label="Appellation de la liste cadeau"
                                >
                                  <el-input
                                    v-model="forms.settings.input.nameCagnotte"
                                    placeholder="Appellation"
                                    :disabled="forms.settings.saving || underMaintenance || !liste.etatCagnotte"
                                  />
                                </el-form-item>
                              </ValidationProvider>
                            </el-col>
                          </el-row>

                          <el-row
                            :gutter="20"
                            type="flex"
                          >
                            <el-col :span="24">
                              <ValidationProvider
                                v-slot="{ errors }"
                                name="cagnotte.typeCagnotte"
                                rules="required"
                              >
                                <el-form-item
                                  :error="errors[0]"
                                  label="J'organise cette liste pour"
                                >
                                  <el-select
                                    v-model="forms.settings.input.typeCagnotte"
                                    placeholder="Type de la cagnotte"
                                    :disabled="forms.settings.saving || underMaintenance || !liste.etatCagnotte"
                                  >
                                    <el-option
                                      v-for="type in typesCagnotte"
                                      :key="type.id"
                                      :value="type.id"
                                      :label="type.name"
                                    />
                                  </el-select>
                                </el-form-item>
                              </ValidationProvider>
                            </el-col>
                          </el-row>

                          <el-row
                            :gutter="20"
                            type="flex"
                          >
                            <el-col :span="24">
                              <ValidationProvider
                                v-slot="{ errors }"
                                name="cagnotte.textPageWeb"
                                rules="max:1500"
                              >
                                <el-form-item
                                  :error="errors[0]"
                                  label="Votre texte de présentation de la liste à destination des participants (1 500 caractères max.)"
                                >
                                  <el-input
                                    v-model="forms.settings.input.textPageWeb"
                                    rows="5"
                                    :placeholder="'Bonjour à toutes et à tous,\n\nVous pouvez participer ci-dessous à cette liste cadeau, ouverte pour offrir un voyage à...'"
                                    type="textarea"
                                    resize="none"
                                    :disabled="forms.settings.saving || underMaintenance || !liste.etatCagnotte"
                                  />
                                </el-form-item>
                              </ValidationProvider>
                            </el-col>
                          </el-row>

                          <el-row
                            :gutter="20"
                            type="flex"
                          >
                            <el-col :span="24">
                              <ValidationProvider
                                v-slot="{ errors }"
                                name="montantVisibleWeb"
                                rules="required"
                              >
                                <el-form-item :error="errors[0]">
                                  <el-checkbox
                                    v-model="forms.settings.input.montantVisibleWeb"
                                    label="Afficher le montant total de la liste cadeau aux futurs participants"
                                    :disabled="forms.settings.saving || underMaintenance || !liste.etatCagnotte"
                                  />
                                </el-form-item>
                              </ValidationProvider>
                            </el-col>
                            <el-col :span="24">
                              <ValidationProvider
                                v-slot="{ errors }"
                                name="boolEmeteurNotifs"
                                rules="required"
                              >
                                <el-form-item :error="errors[0]">
                                  <el-checkbox
                                    v-model="forms.settings.input.boolEmeteurNotifs"
                                    label="Je reçois une notification par e-mail à chaque participation"
                                    :disabled="forms.settings.saving || underMaintenance || !liste.etatCagnotte"
                                  />
                                </el-form-item>
                              </ValidationProvider>
                            </el-col>
                          </el-row>

                          <el-row
                            type="flex"
                          >
                            <el-col
                              align="center"
                              :span="24"
                            >
                              <el-tooltip
                                v-if="!liste.etatCagnotte"
                                content="Vous ne pouvez plus modifier les paramètres car la liste cadeau est clôturée"
                                placement="top"
                              >
                                <el-button
                                  size="small"
                                  type="primary"
                                  disabled
                                >
                                  Enregistrer
                                </el-button>
                              </el-tooltip>
                              <el-button
                                v-else
                                v-loading="forms.settings.saving"
                                size="small"
                                type="primary"
                                :disabled="forms.settings.saving || underMaintenance"
                                @click="passes(saveSettings)"
                              >
                                Enregistrer
                              </el-button>
                            </el-col>
                          </el-row>
                        </el-form>
                      </ValidationObserver>
                    </el-col>
                  </el-row>

                  <!-- Lien de participation de la liste -->
                  <el-form>
                    <el-row />
                  </el-form>
                </el-card>
              </el-row>

              <el-row v-if="liste.etatCagnotte && liste.userIsCreateur()">
                <el-card shadow="never">
                  <template #header>
                    <span>Clôturer cette liste</span>
                  </template>
                  <el-row
                    type="flex"
                  >
                    <el-col :span="24">
                      <p class="vv-liste__close-info">
                        <span>Clôturez cette liste cadeau pour qu'elle puisse être utilisée dans l'une de nos agences.<br>Vous ne pourrez plus collecter de nouvelles participations une fois la liste clôturée.<br>Celle-ci sera valable 12 mois, soit jusqu'au {{ liste.dateValidite | date }}.</span>
                        <span class="vv-liste__close-info-important">Cette action est définitive.</span>
                      </p>
                      <el-row
                        type="flex"
                      >
                        <el-col
                          align="center"
                          :span="24"
                        >
                          <el-button
                            v-loading="forms.close.saving"
                            size="small"
                            type="danger"
                            :disabled="underMaintenance || forms.close.loading"
                            @click="closeListe"
                          >
                            Clôturer cette liste
                          </el-button>
                        </el-col>
                      </el-row>
                    </el-col>
                  </el-row>
                </el-card>
              </el-row>
            </el-col>
          </el-row>
        </el-col>
      </el-row>
    </template>
  </div>
</template>

<script>
// Librairies
import { mapGetters } from 'vuex'
import { saveAs } from 'file-saver'
import ParticipationUrl from '@/components/Liste/ParticipationUrl'

export default {
  components: { ParticipationUrl },
  data () {
    return {
      input: {},
      modals: {
        message: {
          show: false,
          value: {
            name: null,
            texte: null
          }
        },
        created: {
          show: false
        }
      },
      forms: {
        settings: {
          saving: false,
          show: !!this.liste,
          input: {
            nameCagnotte: '',
            typeCagnotte: '',
            boolEmeteurNotifs: false,
            textPageWeb: '',
            montantVisibleWeb: false
          }
        },
        coupon: {
          saving: false,
          show: !!this.liste,
          input: {
            titre: '',
            texte: '',
            showTotal: false,
            showDons: false
          }
        },
        beneficiaireCoupon: {
          saving: false
        },
        close: {
          saving: false
        }
      }
    }
  },

  computed: {
    ...mapGetters(['liste', 'underMaintenance', 'typesCagnotte'])
  },

  watch: {
    liste () {
      this.$nextTick(() => {
        if (this.$refs.donationTable) {
          this.$refs.donationTable.sort('date', 'asc')
        }
      })
    }
  },

  created () {
    // Démarre l'indicateur de chargement
    this.$store.commit('startLoading')

    // Récupère les données utilisées par la vue
    Promise.all([
      this.$store.dispatch('getSystemStatus'),
      this.$store.dispatch('getListe', this.$route.params.id)
    ])
      .then(() => {
        // Initialise l'objet correspondant à la saisie dans le formulaire
        this.resetFormsWithApiData()

        // Affiche la modale si la liste est nouvellement créée
        if (typeof this.$route.query.created !== 'undefined') {
          this.modals.created.show = true

          // Supprime le paramètre de l'URL
          const query = Object.assign({}, this.$route.query)
          delete query.created
          this.$router.replace({ query })
        }
      })
      .catch(this.handleErrorIf404)
      .finally(() => this.$store.commit('stopLoading'))
  },

  methods: {
    refreshListe () {
      this.$store.commit('startLoading')

      this.$store.dispatch('getListe', this.liste.id)
        .then(this.resetFormsWithApiData)
        .catch(this.handleErrorIf404)
        .finally(() => this.$store.commit('stopLoading'))
    },

    handleErrorIf404 (error) {
      if (error && error.response && error.response.status === 404) {
        // Envoi une notification
        this.$message({
          duration: 5000,
          message: `Aucune liste cadeau ne correspond à l'identifiant « ${this.$route.params.id} »`,
          showClose: true,
          type: 'error'
        })

        // Redirige vers la liste des voyages
        this.$router.push({ name: 'Listes' })
      }
    },

    resetFormsWithApiData () {
      if (this.liste) {
        this.forms.settings.input = {
          nameCagnotte: this.liste.nameCagnotte,
          typeCagnotte: this.liste.typeCagnotte,
          boolEmeteurNotifs: this.liste.boolEmeteurNotifs,
          textPageWeb: this.liste.textPageWeb,
          montantVisibleWeb: this.liste.montantVisibleWeb
        }

        if (this.liste.bonCadeau) {
          this.forms.coupon.input = {
            texte: this.liste.bonCadeau.texte,
            titre: this.liste.bonCadeau.title,
            showTotal: this.liste.bonCadeau.montantVisibleBon,
            showDons: this.liste.bonCadeau.showDonsParticipation
          }
        } else {
          this.forms.coupon.input = {
            texte: null,
            titre: '',
            showTotal: false,
            showDons: false
          }
        }
      }
    },

    getSettingInputThatWontEditAnything () {
      return {
        nameCagnotte: this.liste.nameCagnotte,
        typeCagnotte: this.liste.typeCagnotte,
        boolEmeteurNotifs: this.liste.boolEmeteurNotifs,
        textPageWeb: this.liste.textPageWeb,
        etatCagnotte: this.liste.etatCagnotte,
        montantVisibleWeb: this.liste.montantVisibleWeb
      }
    },

    closeListe () {
      this.$confirm('Êtes-vous sûr de vouloir clôturer cette liste cadeau ? Cette action est définitive.', 'Clôturer la liste', {
        confirmButtonText: 'Clôturer',
        cancelButtonText: 'Annuler',
        confirmButtonClass: 'el-button--danger',
        type: 'warning'
      })
        .then(() => {
          this.forms.close.saving = true

          this.$store.dispatch('editListe', {
            id: this.liste.id,
            input: {
              ...this.getSettingInputThatWontEditAnything(),
              etatCagnotte: false
            }
          })
            // .then(() => this.$store.dispatch('getListe', this.$route.params.id))
            .then(() => {
              this.resetFormsWithApiData()

              // Envoi une notification
              this.$message({
                duration: 5000,
                message: 'Liste cadeau clôturée avec succès',
                showClose: true,
                type: 'success'
              })
            })
            .finally(() => {
              this.forms.close.saving = false
            })
        })
    },

    displayCopiedToClipboardMessage () {
      this.$message({
        duration: 5000,
        message: 'Lien vers la liste cadeau copié dans le presse-papier',
        showClose: true,
        type: 'info'
      })
    },

    onDownloadCouponSuccess (pdf) {
      this.$message({
        duration: 5000,
        message: 'Bon cadeau généré avec succès',
        showClose: true,
        type: 'success'
      })

      saveAs(new Blob([pdf], { type: 'application/pdf' }), 'Bon cadeau - VERDIÉ VOYAGES.pdf')
    },

    onDownloadCouponError () {
      this.$message({
        duration: 5000,
        message: 'Une erreur s\'est produite lors du téléchargement du bon cadeau. Merci de rééssayer plus tard.',
        showClose: true,
        type: 'error'
      })
    },

    downloadCouponAsBeneficiaire () {
      this.forms.beneficiaireCoupon.saving = true

      this.$store.dispatch('downloadCoupon', { id: this.liste.id })
        .then(this.onDownloadCouponSuccess)
        .catch(this.onDownloadCouponError)
        .finally(() => {
          this.forms.beneficiaireCoupon.saving = false // Stop l'indicateur de chargement
        })
    },

    saveAndDownloadCoupon () {
      this.forms.coupon.saving = true
      let pdf = null

      this.$store.dispatch('downloadCoupon', { id: this.liste.id, input: { ...this.forms.coupon.input } })
        .then((resultPdf) => {
          pdf = resultPdf
          return this.$store.dispatch('getListe', this.$route.params.id)
        })
        .then(() => {
          this.resetFormsWithApiData()
          this.onDownloadCouponSuccess(pdf)
        })
        .catch(this.onDownloadCouponError)
        .finally(() => {
          this.forms.coupon.saving = false // Stop l'indicateur de chargement
        })
    },

    saveSettings () {
      // Démarre l'indicateur de chargement
      this.forms.settings.saving = true

      // Exécute l'enregistrement auprès de l'api
      this.$store.dispatch('editListe', {
        id: this.liste.id,
        input: {
          ...this.forms.settings.input,
          etatCagnotte: this.liste.etatCagnotte
        }
      })
        // .then(() => this.$store.dispatch('getListe', this.$route.params.id))
        .then(() => {
          this.resetFormsWithApiData()

          // Envoi une notification
          this.$message({
            duration: 5000,
            message: 'Liste cadeau modifiée avec succès',
            showClose: true,
            type: 'success'
          })
        })
        .finally(() => {
          // Stop l'indicateur de chargement
          this.forms.settings.saving = false
        })
    }
  }
}
</script>

<style lang="scss">
.vv-liste {
  &__new {
    &-wrapper {
      display: flex;
      justify-content: flex-end;
      flex-direction: row;
    }

    &-button {
      &-icon {
        font-size: .85rem;
        margin-left: .5rem;
      }
    }
  }

  &__close {
    &-info {
      margin-top: 0;

      &-important {
        font-style: italic;

        > span {
          &:not(:last-child) {
            margin-bottom: .75rem !important;
          }
        }
      }

      > span {
        display: block;

        &:not(:last-child) {
          margin-bottom: 1rem;
        }
      }
    }
  }

  &__message {
    &-text {
      display: block;
      padding: 1rem;
      background-color: #f4f4f4;
    }
  }

  &__columns {
    &-second {
      @media only screen and (max-width: 991px) {
        padding-top: 20px;
      }
      &-multi-row {
        padding-top: 20px;
      }
    }
  }

  &__participants {
    &-wrapper {
      align-items: center;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 10px;

      .el-icon-refresh-right {
        cursor: pointer;
        color: #9cbf4b;
        font-size: 1.8rem;
        display: block;

        @media only screen and (max-width: 991px) {
          font-size: 1.5rem;
        }
      }
    }

    &-mobile {
      @media only screen and (max-width: 991px) {
        display: block;
      }
      @media only screen and (min-width: 991px) {
        display: none;
      }
    }

    &-desktop {
      @media only screen and (max-width: 991px) {
        display: none;
      }
      @media only screen and (min-width: 991px) {
        display: block;
      }
    }

    &-stats {
      margin-top: 20px;

      > .el-col {
        display: flex;
        justify-content: center;
      }

      &-stat {
        display: flex;
        align-items: center;
        flex-direction: column;
        color: #9cbf4b;
        padding: 8px 16px 0px 16px;

        &-title {
          font-size: .95rem;
          font-weight: bolder;
          text-align: center;
        }

        &-value {
          padding: 8px 0;
          font-size: 2rem;
          font-weight: lighter;
          margin-bottom: 5px;
        }
      }
    }

    &-textParticipation {
      padding-right: 10px;
    }
  }

  &__card-nom {
    > .el-card__header {
      padding: 30px 20px;
      text-align: center;
      color: black;
    }

    &-has-no-body {
      > .el-card__body {
        display: none ! important;
      }
    }
  }

  &__modals {
    &-created {
      &-link-info {
        margin-bottom: 7.5px;
      }

      &-eu-info {
        margin-top: 25px;
      }
    }
  }
}
</style>
